/**
 * Builds POST bodies for the delivery `run-submission-pipeline` API from Pharma STP table filters.
 */
import {
  makeSubmissionPipelineIdempotencyKey,
  RunSubmissionPipelineFilters,
  RunSubmissionPipelineRequestBody,
} from "../../../api/postCall/runSubmissionPipeline";
import { FilterValues } from "../../../data-model/filterValues";
import { getOrgIdForFetchExternalWorklist } from "../../../utils/organizationHelper";
import { processFilterData } from "./processFilterData";

/**
 * Converts a BigQuery-style YYYYMMDD date string to YYYY-MM-DD for the delivery pipeline.
 *
 * @param ymd - Eight-digit date or already ISO-shaped string
 * @returns ISO date (YYYY-MM-DD) or undefined if input is invalid
 */
const yyyymmddToIsoDate = (ymd: string): string | undefined => {
  if (!ymd) {
    return undefined;
  }
  if (/^\d{8}$/.test(ymd)) {
    return `${ymd.slice(0, 4)}-${ymd.slice(4, 6)}-${ymd.slice(6, 8)}`;
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(ymd)) {
    return ymd;
  }
  return undefined;
};

/**
 * Maps the Pharma STP file table Redux filters to the rpa-service submission pipeline filter shape.
 *
 * The backend rejects non-dry-run calls with an empty `filters` object (see
 * `DeliveryPipelineRequest` in `pharmacy_rpa`), so callers must confirm
 * {@link hasDeliveryPipelineFilters} before invoking the pipeline.
 *
 * @param validFilters - Filters scoped to {@link TableNames.PHARMA_STP_FILE_ORDERS}
 * @param filterOverrides - Optional pipeline filter fields that override Redux-derived values (e.g. selected filenames or modal date range)
 * @return Filters in the shape the delivery pipeline API expects
 */
export const buildDeliveryPipelineFilters = (
  validFilters: FilterValues[],
  filterOverrides?: Partial<RunSubmissionPipelineFilters>,
): RunSubmissionPipelineFilters => {
  const { filters: processed, dateRange } = processFilterData(validFilters);
  const filters: RunSubmissionPipelineFilters = {};

  if (processed.filename?.length) {
    filters.filename = processed.filename;
  }
  if (processed.poc?.length) {
    filters.form_name = processed.poc;
  }
  if (dateRange?.startDate) {
    const start = yyyymmddToIsoDate(dateRange.startDate);
    if (start) {
      filters.date_of_filing_start = start;
    }
  }
  if (dateRange?.endDate) {
    const end = yyyymmddToIsoDate(dateRange.endDate);
    if (end) {
      filters.date_of_filing_end = end;
    }
  }

  if (filterOverrides) {
    (
      Object.entries(filterOverrides) as [
        keyof RunSubmissionPipelineFilters,
        unknown,
      ][]
    ).forEach(([key, value]) => {
      if (value === undefined) {
        return;
      }
      if (Array.isArray(value) && value.length === 0) {
        return;
      }
      filters[key] = value as RunSubmissionPipelineFilters[typeof key];
    });
  }

  return filters;
};

/**
 * Whether the current table filters (plus any overrides) produce at least one
 * delivery-pipeline filter field. Gates the "Run Delivery Pipeline" action,
 * since the backend requires a non-empty `filters` for non-dry-run calls.
 *
 * @param validFilters - Filters scoped to {@link TableNames.PHARMA_STP_FILE_ORDERS}
 * @param filterOverrides - Optional pipeline filter fields that override Redux-derived values
 * @return True when at least one qualifying filter field is present
 */
export const hasDeliveryPipelineFilters = (
  validFilters: FilterValues[],
  filterOverrides?: Partial<RunSubmissionPipelineFilters>,
): boolean =>
  Object.keys(buildDeliveryPipelineFilters(validFilters, filterOverrides))
    .length > 0;

/**
 * Builds the POST body for {@link runSubmissionPipeline} from the Pharma STP table filters.
 *
 * @param validFilters - Filters scoped to {@link TableNames.PHARMA_STP_FILE_ORDERS}
 * @param pipelineScope - Suffix for idempotency (e.g. active tab or modal mode)
 * @param filterOverrides - Optional pipeline filter fields that override Redux-derived values (e.g. selected filenames or modal date range)
 * @return Request body for {@link runSubmissionPipeline}
 */
export const buildRunSubmissionPipelinePayload = (
  validFilters: FilterValues[],
  pipelineScope: string,
  filterOverrides?: Partial<RunSubmissionPipelineFilters>,
): RunSubmissionPipelineRequestBody => ({
  org_id: getOrgIdForFetchExternalWorklist(),
  filters: buildDeliveryPipelineFilters(validFilters, filterOverrides),
  dry_run: false,
  idempotency_key: makeSubmissionPipelineIdempotencyKey(pipelineScope),
});
